import type {
  PlayerCardDistribution,
  PlayerCardDistributionDivision,
  PlayerCardSourceDivision,
} from '@/types';

/** player_cards.rarity → variant name (0=Rookie … 3=Legendary). */
export const CARD_VARIANTS = ['Rookie', 'Semi-Pro', 'Pro', 'Legendary'];

/** A count in Indian grouping (1,17,509), or a dash when the server sent none. */
export const fmtCount = (value?: number | null) =>
  value === undefined || value === null ? '—' : value.toLocaleString('en-IN');

/** `part` as a share of `whole` ("12.3%"), or null when either is missing or whole is 0. */
export const shareOf = (part?: number | null, whole?: number | null) =>
  part === undefined || part === null || whole === undefined || whole === null || whole === 0
    ? null
    : `${((part / whole) * 100).toFixed(1)}%`;

/** "IAP 127 · coins 266", or a dash when the server sent neither count (older backend). */
export const boughtLabel = (iap?: number | null, coins?: number | null) =>
  (iap === undefined || iap === null) && (coins === undefined || coins === null)
    ? '—'
    : `IAP ${fmtCount(iap)} · coins ${fmtCount(coins)}`;

const num = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** Cards not bought with money or coins: the server's `earned`, else cards − IAP − coins. */
export const earnedOf = (r: {
  cards?: number | null;
  boughtIap?: number | null;
  boughtCoins?: number | null;
  earned?: number | null;
}): number | undefined => {
  const earned = num(r.earned);
  if (earned !== undefined) return earned;
  const cards = num(r.cards);
  const iap = num(r.boughtIap);
  const coins = num(r.boughtCoins);
  return cards === undefined || iap === undefined || coins === undefined
    ? undefined
    : Math.max(0, cards - iap - coins);
};

/** player_cards.source values the server stamps on new cards. */
export const CARD_SOURCE_LABELS: Record<string, string> = {
  match_drop: 'Match-win drop',
  quick_play: 'Quick Play',
  level_up: 'Level-up',
  daily_deal: 'Daily deal',
  daily_engagement: 'Daily engagement',
  ftue: 'FTUE',
  starter: 'Starter',
  iap: 'Bought (IAP)',
  coins: 'Bought (coins)',
  leaderboard_prize: 'Leaderboard prize',
  reward: 'Reward',
  admin: 'Admin',
  generate_client: 'Generate (client rarity)',
  generate_server: 'Generate (server rarity)',
  other: 'Other',
};

/** A readable name for a source key; unknown keys are shown as sent. */
export const sourceLabel = (source: string) => CARD_SOURCE_LABELS[source] ?? source;

export interface SourceCount {
  source: string;
  label: string;
  count: number;
}

/** Source counts, largest first (ties by label), zeros and non-numbers left out. */
export const sortedSources = (counts?: Record<string, unknown> | null): SourceCount[] =>
  Object.entries(counts ?? {})
    .map(([source, value]) => ({ source, label: sourceLabel(source), count: num(value) ?? 0 }))
    .filter((s) => s.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

/** Every source that appears in any division, in the order of their overall totals. */
export const sourceColumnsOf = (rows: PlayerCardSourceDivision[]): SourceCount[] => {
  const totals: Record<string, number> = {};
  for (const row of rows) {
    for (const [source, value] of Object.entries(row.counts ?? {})) {
      totals[source] = (totals[source] ?? 0) + (num(value) ?? 0);
    }
  }
  return sortedSources(totals);
};

const asObject = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asCounts = (value: unknown): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [key, v] of Object.entries(asObject(value))) {
    const n = num(v);
    if (n !== undefined) out[key] = n;
  }
  return out;
};

/**
 * The distribution response with missing parts filled in, so the page never
 * crashes on a partial body: lists become [], counts 0, strings null.
 * Division rows are sorted by tier.
 */
export const normaliseDistribution = (raw: unknown): PlayerCardDistribution => {
  const body = asObject(raw);
  const rarity = num(body.rarity);
  const divisions: PlayerCardDistributionDivision[] = (
    Array.isArray(body.ownersByDivision) ? body.ownersByDivision : []
  )
    .map((d) => asObject(d))
    .filter((d) => num(d.tier) !== undefined)
    .map((d) => ({
      tier: num(d.tier) as number,
      name: typeof d.name === 'string' && d.name ? d.name : `Division ${String(d.tier)}`,
      owners: num(d.owners) ?? 0,
      cards: num(d.cards) ?? 0,
      boughtIap: num(d.boughtIap) ?? 0,
      boughtCoins: num(d.boughtCoins) ?? 0,
    }))
    .sort((a, b) => a.tier - b.tier);
  const bySourceDivision: PlayerCardSourceDivision[] = (
    Array.isArray(body.cardsBySourceDivision) ? body.cardsBySourceDivision : []
  )
    .map((d) => asObject(d))
    .filter((d) => num(d.tier) !== undefined)
    .map((d) => ({ tier: num(d.tier) as number, counts: asCounts(d.counts) }))
    .sort((a, b) => a.tier - b.tier);
  const cards = num(body.cards) ?? 0;
  const boughtIap = num(body.boughtIap) ?? 0;
  const boughtCoins = num(body.boughtCoins) ?? 0;
  return {
    playerIndex: num(body.playerIndex) ?? -1,
    displayName: typeof body.displayName === 'string' ? body.displayName : '',
    rarity: rarity === undefined ? null : rarity,
    computedAt: typeof body.computedAt === 'string' ? body.computedAt : null,
    owners: num(body.owners) ?? 0,
    cards,
    boughtIap,
    boughtCoins,
    earned: earnedOf({ cards, boughtIap, boughtCoins, earned: num(body.earned) }) ?? 0,
    ownersByDivision: divisions,
    sourceTrackedSince:
      typeof body.sourceTrackedSince === 'string' ? body.sourceTrackedSince : null,
    cardsBySource: asCounts(body.cardsBySource),
    cardsBySourceDivision: bySourceDivision,
  };
};
